import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, Paper, Snackbar, TextField, Typography } from "@mui/material";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import pluginServices from "app/services/plugin-services";
import authServices from "app/services/auth-services";
import { StandardFiltersBar, StandardPageHeader, StandardSearchField } from "app/shared/ui";
import { hasCurrentProfile } from "app/utils/appHelpers";
import PluginCard from "./PluginCard";
import PluginConfigDialog from "./PluginConfigDialog";
import PluginRequestDialog from "./PluginRequestDialog";
import { categoriesOf, installedFirst, matchesSearch, pluginErrorMessage } from "./pluginHelpers";

// Plugins de la empresa en una sola grilla, primero los instalados (activar, configurar, desinstalar) y después los que
// se pueden instalar o solicitar, con buscador y filtros de estado y categoría. Superadmin decide cuáles están
// disponibles para cada empresa.
const PluginsIndex = () => {
    const { authUser, setAuthData } = useJumboAuth();
    const isSuperadmin = hasCurrentProfile(authUser, ["superadmin"]);
    const [plugins, setPlugins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch] = useState("");
    const [onlyInstalled, setOnlyInstalled] = useState(false);
    const [category, setCategory] = useState("");
    const [busyId, setBusyId] = useState(null);
    const [configuring, setConfiguring] = useState(null);
    const [requesting, setRequesting] = useState(null);
    const [toast, setToast] = useState(null);

    const load = useCallback(() => pluginServices.getPlugins()
        .then((response) => {
            const list = response?.data || [];
            setPlugins(list);
            setLoadError(null);
            return list;
        })
        .catch(() => {
            setLoadError("No se pudieron cargar los plugins.");
            return null;
        })
        .finally(() => setLoading(false)), []);

    useEffect(() => { load(); }, [load]);

    // El menú lateral muestra secciones según los plugins activos: se recarga el usuario después de cada cambio.
    const refreshUser = () => authServices.getCurrentUser()
        .then((user) => { if (user) setAuthData({ authUser: user }); })
        .catch(() => {});

    const afterChange = (message, severity = "success") => {
        setToast({ message, severity });
        refreshUser();
        return load();
    };

    const categories = useMemo(() => categoriesOf(plugins), [plugins]);
    const installedCount = plugins.filter((plugin) => plugin.installed).length;
    const visible = useMemo(() => installedFirst(plugins.filter((plugin) => (
        (!onlyInstalled || plugin.installed) && (!category || plugin.category === category) && matchesSearch(plugin, search)
    ))), [plugins, onlyInstalled, category, search]);
    const filtering = !!search.trim() || onlyInstalled || !!category;

    const run = async (plugin, action) => {
        setBusyId(plugin.id);
        try {
            await action();
        } catch (requestError) {
            setToast({ severity: "error", message: pluginErrorMessage(requestError, "No se pudo completar el cambio.") });
            load();
        } finally {
            setBusyId(null);
        }
    };

    const install = (plugin) => run(plugin, async () => {
        const result = await pluginServices.togglePluginInstallation(plugin.id, { activate: true });
        if (result?.requires_configuration) {
            const list = await afterChange(`«${plugin.name}» quedó instalado. Complétale la configuración para activarlo.`, "info");
            const fresh = list?.find((item) => item.id === plugin.id);
            if (fresh) setConfiguring({ plugin: fresh, notice: "Completa los campos requeridos para activarlo." });
        } else if (result?.enabled) {
            await afterChange(`«${plugin.name}» quedó instalado y activo.`);
        } else {
            await afterChange(`«${plugin.name}» quedó instalado, pero no se pudo activar${result?.activation_error ? `: ${result.activation_error}` : "."}`, "warning");
        }
    });

    const uninstall = (plugin) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`¿Desinstalar «${plugin.name}»? Deja de funcionar para toda la empresa y se borra su configuración.`)) return;
        run(plugin, async () => {
            await pluginServices.togglePluginInstallation(plugin.id);
            await afterChange(`«${plugin.name}» quedó desinstalado.`, "info");
        });
    };

    const toggleActive = (plugin) => {
        if (!plugin.enabled && plugin.missing_required_fields?.length) {
            setConfiguring({ plugin, notice: "Completa los campos requeridos para activarlo." });
            return;
        }
        run(plugin, async () => {
            await pluginServices.togglePluginActivation(plugin.id);
            await afterChange(plugin.enabled ? `«${plugin.name}» quedó inactivo.` : `«${plugin.name}» quedó activo.`, plugin.enabled ? "info" : "success");
        });
    };

    const configSaved = (plugin, activated) => {
        setConfiguring(null);
        afterChange(activated ? `Configuración guardada y «${plugin.name}» activo.` : `Configuración de «${plugin.name}» guardada.`);
    };

    const renderCard = (plugin) => (
        <PluginCard
            plugin={plugin}
            busy={busyId === plugin.id}
            isSuperadmin={isSuperadmin}
            onInstall={install}
            onUninstall={uninstall}
            onToggleActive={toggleActive}
            onConfigure={(target) => setConfiguring({ plugin: target, notice: null })}
            onRequest={setRequesting}
        />
    );

    const renderGrid = () => {
        if (!visible.length) {
            return (
                <Paper sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
                    <Typography variant="h6" mb={1}>No hay plugins para mostrar</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {filtering ? "Prueba con otra búsqueda o cambia los filtros." : "Aún no hay plugins para tu empresa."}
                    </Typography>
                </Paper>
            );
        }
        return (
            <Grid container spacing={2.5}>
                {visible.map((plugin) => (
                    <Grid item xs={12} sm={6} lg={4} key={plugin.id}>{renderCard(plugin)}</Grid>
                ))}
            </Grid>
        );
    };

    return (
        <>
            <StandardPageHeader
                title="Plugins"
                subtitle={loading || loadError ? "Suma funciones a tu empresa: instálalas, actívalas y configúralas aquí."
                    : `${installedCount} de ${plugins.length} plugins instalados.`}
            />

            <StandardFiltersBar>
                <Grid container spacing={1.25} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <StandardSearchField
                            fullWidth
                            placeholder="Buscar por nombre o descripción"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField select size="small" fullWidth label="Estado" value={onlyInstalled ? "installed" : ""}
                                   onChange={(event) => setOnlyInstalled(event.target.value === "installed")}>
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="installed">Instalados</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField select size="small" fullWidth label="Categoría" value={category}
                                   onChange={(event) => setCategory(event.target.value)}>
                            <MenuItem value="">Todas</MenuItem>
                            {categories.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                        </TextField>
                    </Grid>
                </Grid>
            </StandardFiltersBar>

            {loading ? (
                <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress /></Box>
            ) : loadError ? (
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => { setLoading(true); load(); }}>Reintentar</Button>}>
                    {loadError}
                </Alert>
            ) : renderGrid()}

            <PluginConfigDialog
                plugin={configuring?.plugin}
                notice={configuring?.notice}
                onClose={() => setConfiguring(null)}
                onSaved={configSaved}
            />
            <PluginRequestDialog
                plugin={requesting}
                onClose={() => setRequesting(null)}
                onSent={(plugin) => { setRequesting(null); setToast({ severity: "success", message: `Enviamos tu solicitud de «${plugin.name}». Te contactaremos pronto.` }); }}
            />

            <Snackbar open={!!toast} autoHideDuration={toast?.severity === "error" ? 8000 : 5000} onClose={() => setToast(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ width: "100%" }}>{toast.message}</Alert> : <span />}
            </Snackbar>
        </>
    );
};

export default PluginsIndex;
